import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, Input, Form, message, Typography } from 'antd';
import { PhoneOutlined, SafetyOutlined } from '@ant-design/icons';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import Loader from '../Loader/Loader';
import {
  clearRecaptchaVerifier,
  isRecaptchaVerificationError,
  reopenRecaptchaWidget,
  resetRecaptchaWidget,
} from './recaptcha';
import { reportPhoneVerificationError } from './phoneVerificationError';

const { Title, Text } = Typography;

interface MFAVerificationProps {
  resolver?: firebase.auth.MultiFactorResolver | null;
  onSuccess: () => void;
  onRequiresRecentLogin: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({
  resolver,
  onSuccess,
  onRequiresRecentLogin,
}) => {
  const [form] = Form.useForm();
  const [initialLoading, setInitialLoading] = useState(true);
  const [verifyCodeLoading, setVerifyCodeLoading] = useState(false);
  const [verificationId, setVerificationId] = useState<string>('');
  const phoneNumber = useMemo(() => {
    if (resolver && resolver.hints && resolver.hints[0]) {
      const hint = resolver.hints[0];
      if ('phoneNumber' in hint) {
        return (hint as any).phoneNumber;
      }
    }
    return '';
  }, [resolver]);
  const [smsResendCooldown, setSmsResendCooldown] = useState<number>(0);
  const [smsResendLoading, setSmsResendLoading] = useState(false);
  const [isRecaptchaInitialized, setIsRecaptchaInitialized] = useState(false);
  const [isRecaptchaRequestPending, setIsRecaptchaRequestPending] =
    useState(false);
  const recaptchaVerifier = useRef<firebase.auth.RecaptchaVerifier | null>(
    null,
  );
  const recaptchaWidgetId = useRef<number | null>(null);
  const sendCodeInFlightRef = useRef(false);
  const hasAutoSentRef = useRef(false);
  const smsCooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recaptchaInitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Suppress auto-send until this resolver's verifier has rendered.
    hasAutoSentRef.current = true;
    setIsRecaptchaInitialized(false);
    setIsRecaptchaRequestPending(false);
    setVerificationId('');
    setInitialLoading(true);

    const initializeRecaptcha = () => {
      if (cancelled) {
        return;
      }

      try {
        const container = document.getElementById('mfa-recaptcha-container');
        if (!container) {
          recaptchaInitTimeoutRef.current = setTimeout(
            initializeRecaptcha,
            100,
          );
          return;
        }

        clearRecaptchaVerifier(recaptchaVerifier.current, container);
        recaptchaVerifier.current = new firebase.auth.RecaptchaVerifier(
          'mfa-recaptcha-container',
          {
            size: 'invisible',
            badge: 'inline',
            'expired-callback': () => {
              resetRecaptchaWidget(recaptchaWidgetId.current);
              message.error('reCAPTCHA expired. Please try again.');
            },
            'error-callback': () => {
              resetRecaptchaWidget(recaptchaWidgetId.current);
              message.error('reCAPTCHA error. Please try again.');
            },
          },
        );

        recaptchaVerifier.current
          .render()
          .then((widgetId: any) => {
            if (cancelled) {
              return;
            }

            recaptchaWidgetId.current = widgetId;
            hasAutoSentRef.current = false;
            setIsRecaptchaInitialized(true);
          })
          .catch((error: any) => {
            if (cancelled) {
              return;
            }

            console.error('Failed to render reCAPTCHA:', error);
            message.error(
              'Failed to initialize reCAPTCHA. Please refresh the page.',
            );
            setInitialLoading(false);
          });
      } catch (e) {
        console.error('Failed to initialize reCAPTCHA:', e);
        message.error(
          'Failed to initialize reCAPTCHA. Please refresh the page.',
        );
        setInitialLoading(false);
      }
    };

    recaptchaInitTimeoutRef.current = setTimeout(initializeRecaptcha, 100);

    return () => {
      cancelled = true;
      clearRecaptchaVerifier(
        recaptchaVerifier.current,
        document.getElementById('mfa-recaptcha-container'),
      );
      recaptchaVerifier.current = null;
      recaptchaWidgetId.current = null;

      // Clean up interval
      if (smsCooldownIntervalRef.current) {
        clearInterval(smsCooldownIntervalRef.current);
      }

      // Clean up all timeouts
      if (recaptchaInitTimeoutRef.current) {
        clearTimeout(recaptchaInitTimeoutRef.current);
      }
    };
  }, [resolver]);

  useEffect(() => {
    if (isRecaptchaInitialized && !hasAutoSentRef.current) {
      hasAutoSentRef.current = true;

      if (phoneNumber) {
        handleSendCode(
          () => null,
          () => setInitialLoading(false),
        );
      } else {
        setInitialLoading(false);
      }
    }
  }, [isRecaptchaInitialized, phoneNumber]);

  const handleSendCode = async (
    onLoadStart: () => void,
    onLoadEnd: () => void,
  ) => {
    if (sendCodeInFlightRef.current) {
      return;
    }

    if (smsResendCooldown > 0) {
      message.warning(
        `Please wait ${smsResendCooldown} seconds before requesting another SMS code.`,
      );
      return;
    }

    if (
      !(
        resolver &&
        resolver.session &&
        resolver.hints &&
        resolver.hints[0] &&
        'phoneNumber' in resolver.hints[0]
      )
    ) {
      message.error('MFA resolver not available');
      return;
    }

    sendCodeInFlightRef.current = true;
    onLoadStart();

    try {
      // Every SMS attempt must start with a fresh token. A failed request for a
      // previous phone number can otherwise leave Firebase with a stale token.
      resetRecaptchaWidget(recaptchaWidgetId.current);

      if (!recaptchaVerifier.current) {
        throw new Error('reCAPTCHA verifier not initialized');
      }

      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session,
      };

      setIsRecaptchaRequestPending(true);
      const newVerificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier.current,
      );

      setVerificationId(newVerificationId);
      message.success('Verification code sent to your phone');

      setSmsResendCooldown(120);

      if (smsCooldownIntervalRef.current) {
        clearInterval(smsCooldownIntervalRef.current);
      }

      smsCooldownIntervalRef.current = setInterval(() => {
        setSmsResendCooldown((prev) => {
          if (prev <= 1) {
            if (smsCooldownIntervalRef.current) {
              clearInterval(smsCooldownIntervalRef.current);
              smsCooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      resetRecaptchaWidget(recaptchaWidgetId.current);

      if (isRecaptchaVerificationError(error)) {
        message.error(
          'Security verification expired or was rejected. Try sending the code again; a reCAPTCHA challenge may appear.',
        );
      } else if (error.code === 'auth/too-many-requests') {
        message.error(
          'Too many requests. Please wait 2 minutes before trying again.',
        );
        setSmsResendCooldown(120);

        if (smsCooldownIntervalRef.current) {
          clearInterval(smsCooldownIntervalRef.current);
        }

        smsCooldownIntervalRef.current = setInterval(() => {
          setSmsResendCooldown((prev) => {
            if (prev <= 1) {
              if (smsCooldownIntervalRef.current) {
                clearInterval(smsCooldownIntervalRef.current);
                smsCooldownIntervalRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (error.code === 'auth/requires-recent-login') {
        onRequiresRecentLogin();
      } else {
        const reportedError = reportPhoneVerificationError(
          error,
          'mfa-sign-in-start',
        );
        if (reportedError) {
          message.error(
            `${reportedError.userMessage} Incident ID: ${reportedError.incidentId}`,
            10,
          );
        } else {
          message.error(error.message || 'Failed to send verification code');
        }
      }
    } finally {
      sendCodeInFlightRef.current = false;
      setIsRecaptchaRequestPending(false);
      onLoadEnd();
    }
  };

  const handleReopenRecaptcha = () => {
    if (!reopenRecaptchaWidget(recaptchaWidgetId.current)) {
      message.error(
        'Unable to reopen the security check. Please refresh the page.',
      );
    }
  };

  const handleVerificationSubmit = async (values: {
    verificationCode: string;
  }) => {
    setVerifyCodeLoading(true);
    try {
      if (!resolver) {
        throw new Error('MFA resolver not available');
      }

      const phoneCredential = firebase.auth.PhoneAuthProvider.credential(
        verificationId,
        values.verificationCode,
      );

      const multiFactorAssertion =
        firebase.auth.PhoneMultiFactorGenerator.assertion(phoneCredential);

      await resolver.resolveSignIn(multiFactorAssertion);

      message.success('Multi-factor authentication verified successfully!');
      onSuccess();
    } catch (error: any) {
      if (error.code === 'auth/invalid-verification-code') {
        message.error('Incorrect SMS code! Please try again.');
      } else if (error.code === 'auth/requires-recent-login') {
        onRequiresRecentLogin();
      } else {
        const reportedError = reportPhoneVerificationError(
          error,
          'mfa-sign-in-finalize',
        );
        if (reportedError) {
          message.error(
            `${reportedError.userMessage} Incident ID: ${reportedError.incidentId}`,
            10,
          );
        } else {
          message.error(
            error.message || 'Failed to verify multi-factor authentication',
          );
        }
      }
    } finally {
      setVerifyCodeLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(
        4,
        7,
      )}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  return (
    <div>
      {initialLoading ? (
        <>
          <Loader />
          {isRecaptchaRequestPending && (
            <div style={{ textAlign: 'center' }}>
              <Button type="link" onClick={handleReopenRecaptcha}>
                Reopen security check
              </Button>
            </div>
          )}
        </>
      ) : (
        <div style={{ maxWidth: 400, margin: '0 auto', padding: '20px' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <SafetyOutlined
              style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }}
            />
            <Title level={3}>Verify Your Identity</Title>
            <Text type="secondary">
              {verificationId
                ? 'Enter the verification code sent to your phone number'
                : 'Request a verification code to continue'}
            </Text>
          </div>

          <div
            style={{
              textAlign: 'center',
              marginBottom: 24,
              padding: 16,
              backgroundColor: '#f5f5f5',
              borderRadius: 8,
            }}
          >
            <PhoneOutlined style={{ marginRight: 8 }} />
            <Text strong>{formatPhoneNumber(phoneNumber)}</Text>
          </div>

          <Form
            form={form}
            onFinish={handleVerificationSubmit}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="verificationCode"
              label="Verification Code"
              rules={[
                {
                  required: true,
                  message: 'Please enter the verification code',
                },
                {
                  pattern: /^\d{6}$/,
                  message: 'Please enter a valid 6-digit code',
                },
              ]}
            >
              <Input
                placeholder="123456"
                maxLength={6}
                disabled={verifyCodeLoading || !verificationId}
                style={{ textAlign: 'center', fontSize: 18, letterSpacing: 4 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={verifyCodeLoading}
                disabled={verifyCodeLoading || !verificationId}
                block
                size="large"
              >
                Verify Code
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Button
              type="link"
              onClick={() =>
                handleSendCode(
                  () => setSmsResendLoading(true),
                  () => setSmsResendLoading(false),
                )
              }
              disabled={
                smsResendLoading || verifyCodeLoading || !isRecaptchaInitialized
              }
              loading={smsResendLoading}
            >
              {smsResendCooldown > 0
                ? `Resend code (${Math.floor(smsResendCooldown / 60)}:${(
                    smsResendCooldown % 60
                  )
                    .toString()
                    .padStart(2, '0')})`
                : verificationId
                ? 'Resend verification code'
                : 'Send verification code'}
            </Button>
            {isRecaptchaRequestPending && (
              <Button type="link" onClick={handleReopenRecaptcha}>
                Reopen security check
              </Button>
            )}
          </div>
        </div>
      )}
      <Text className="recaptcha-disclosure" type="secondary">
        This site is protected by reCAPTCHA and the Google{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          Terms of Service
        </a>{' '}
        apply.
      </Text>
      {/* Keep Google-owned widget DOM scoped to this component. */}
      <div id="mfa-recaptcha-container" className="recaptcha-container"></div>
    </div>
  );
};

export default MFAVerification;
